import { useMemo } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import { enrollmentStatusLabel, activityLabel } from '../../../utility/enrollmentsBilling'

import classes from './EnrollmentDetailDrawer.module.scss'

// An enrollment's detail rows plus its "All Updates" timeline. Shared by the Enrollments & Billing
// drawer and the Members drawer, so both show the same fields and the same merged activity list.
const EnrollmentDetailsCard = ({ enrollment, displayStatus }) => {
  const { t } = useTranslation()
  const { course, package: pkg } = enrollment

  const isPending = displayStatus === 'pending_approval'
  const isWaitlisted = displayStatus === 'waitlisted'
  const sectionTitle = (isPending || isWaitlisted) ? t('enrollmentsBilling.drawer.details') : t('enrollmentsBilling.drawer.enrollmentDetails')
  const showPackage = !isWaitlisted && !!pkg
  const showStatusBadge = !isPending && !isWaitlisted

  // Enrollment and invoice activities are separate lists; merge them chronologically (newest first).
  const allActivities = useMemo(() => {
    const merged = [
      ...(enrollment.enrollmentActivities || []).map(a => ({ ...a, source: 'enrollment' })),
      ...(enrollment.invoiceActivities || []).map(a => ({ ...a, source: 'invoice' })),
    ]
    return merged.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  }, [enrollment.enrollmentActivities, enrollment.invoiceActivities])

  const familyAdminEmail = enrollment.student?.familyAdmins?.[0]?.email

  return (
    <section className={classes.detailCard}>
      <div className={classes.detailCardHeader}>
        <h6 className={classNames(classes.sectionTitle, classes.cardTitle)}>{sectionTitle}</h6>
        {showStatusBadge && (
          <span className={classNames(classes.statusBadge, classes[`status_${displayStatus}`])}>
            {enrollmentStatusLabel(displayStatus, t)}
          </span>
        )}
      </div>

      <div className={classes.detailRow}>
        <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.program')}</span>
        <span className={classes.detailValue}>{course?.name}</span>
      </div>

      {showPackage && (
        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.package')}</span>
          <span className={classes.detailValue}>{pkg.name}</span>
        </div>
      )}

      <div className={classes.detailRow}>
        <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.facility')}</span>
        <span className={classes.detailValue}>{course?.communityName || '-'}</span>
      </div>

      <div className={classes.detailRow}>
        <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.coach')}</span>
        <span className={classes.detailValue}>{course?.coachName || '-'}</span>
      </div>

      {familyAdminEmail && (
        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.familyAdminEmail')}</span>
          <span className={classes.detailValue}>{familyAdminEmail}</span>
        </div>
      )}

      {course?.startDate && (
        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>{t('enrollmentsBilling.drawer.programDates')}</span>
          <span className={classes.detailValue}>
            {moment(course.startDate).format('MM/DD/YYYY')}
            {course.endDate && `-${moment(course.endDate).format('MM/DD/YYYY')}`}
          </span>
        </div>
      )}

      {allActivities.length > 0 && (
        <div className={classes.updatesBlock}>
          <h6 className={classes.sectionTitle}>{t('enrollmentsBilling.drawer.allUpdates')}</h6>
          <div className={classes.activityTimeline}>
            {allActivities.map(activity => (
              <div key={`${activity.source}-${activity.id}`} className={classes.activityItem}>
                <div className={classes.activityDot} />
                <div className={classes.activityContent}>
                  <span className={classes.activityDate}>
                    {moment(activity.createdAt).format('MM/DD/YYYY')}
                  </span>
                  <span className={classes.activityLabel}>
                    {activityLabel(activity.changeType, t)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

EnrollmentDetailsCard.propTypes = {
  enrollment: PropTypes.object.isRequired,
  displayStatus: PropTypes.string,
}

export default EnrollmentDetailsCard
